// features/devices/deviceSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { deviceAPI } from '../../features/devices/deviceAPI';

// ==================== ASYNC THUNKS ====================

// Fetch devices based on role
export const fetchDevices = createAsyncThunk(
  'devices/fetchDevices',
  async (_, { rejectWithValue }) => {
    try {
      const userRole = deviceAPI.getUserRole();
      const isAdmin = userRole === 'superadmin' || userRole === 'Admin' || userRole === 'admin';
      
      let devices;
      
      if (isAdmin) {
        devices = await deviceAPI.getAllDevices();
        console.log("Admin fetched devices count:", devices?.length || 0);
      } else {
        const dealerId = deviceAPI.getDealerId();
        if (dealerId) {
          devices = await deviceAPI.getDealerDevices(dealerId);
          console.log(`User fetched devices for dealer ${dealerId}:`, devices?.length || 0);
        } else {
          devices = [];
        }
      }
      
      return { devices, userRole, isAdmin };
    } catch (error) {
      console.error("Fetch devices error:", error);
      return rejectWithValue(error.message || 'Failed to fetch devices');
    }
  }
);

// Fetch dealer-specific devices
export const fetchDealerDevices = createAsyncThunk(
  'devices/fetchDealerDevices',
  async (dealerId, { rejectWithValue }) => {
    try {
      const devices = await deviceAPI.getDealerDevices(dealerId);
      return { devices, isAdmin: false };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch dealer devices');
    }
  }
);

// Create device
export const createDevice = createAsyncThunk(
  'devices/createDevice',
  async (deviceData, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceAPI.createDevice(deviceData);
      console.log("Create device response:", response);
      
      // Refresh devices after successful creation
      const userRole = deviceAPI.getUserRole();
      const isAdmin = userRole === 'superadmin' || userRole === 'Admin' || userRole === 'admin';
      
      if (isAdmin) {
        await dispatch(fetchDevices());
      } else {
        const dealerId = deviceAPI.getDealerId();
        if (dealerId) {
          await dispatch(fetchDealerDevices(dealerId));
        } else {
          await dispatch(fetchDevices());
        }
      }
      
      return { success: true, message: 'Device added successfully!' };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create device');
    }
  }
);

// Edit device
export const editDevice = createAsyncThunk(
  'devices/editDevice',
  async ({ id, devdata }, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceAPI.editDevice(id, devdata);
      console.log("Edit device response:", response);
      
      // Refresh devices after successful edit
      const userRole = deviceAPI.getUserRole();
      const isAdmin = userRole === 'superadmin' || userRole === 'Admin' || userRole === 'admin';
      
      if (isAdmin) {
        await dispatch(fetchDevices());
      } else {
        const dealerId = deviceAPI.getDealerId();
        if (dealerId) {
          await dispatch(fetchDealerDevices(dealerId));
        } else {
          await dispatch(fetchDevices());
        }
      }
      
      return { success: true, message: 'Device updated successfully!' };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to edit device');
    }
  }
);

// Delete device
export const deleteDevice = createAsyncThunk(
  'devices/deleteDevice',
  async (devId, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceAPI.deleteDevice(devId);
      console.log("Delete device response:", response);
      
      // Refresh devices after successful deletion
      const userRole = deviceAPI.getUserRole();
      const isAdmin = userRole === 'superadmin' || userRole === 'Admin' || userRole === 'admin';
      
      if (isAdmin) {
        await dispatch(fetchDevices());
      } else {
        const dealerId = deviceAPI.getDealerId();
        if (dealerId) {
          await dispatch(fetchDealerDevices(dealerId));
        } else {
          await dispatch(fetchDevices());
        }
      }
      
      return { success: true, message: 'Device deleted successfully!' };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete device');
    }
  }
);

// Get device office
export const getDeviceOffice = createAsyncThunk(
  'devices/getDeviceOffice',
  async (sno, { rejectWithValue }) => {
    try {
      const officeData = await deviceAPI.getDeviceOffice(sno);
      return officeData;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch device office info');
    }
  }
);

// Get serial number
export const getSerialNumber = createAsyncThunk(
  'devices/getSerialNumber',
  async (state, { rejectWithValue }) => {
    try {
      const serialNumber = await deviceAPI.getSerialNumber(state);
      return serialNumber;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get serial number');
    }
  }
);

// Get device config
export const getDeviceConfig = createAsyncThunk(
  'devices/getDeviceConfig',
  async (cbid, { rejectWithValue }) => {
    try {
      const configData = await deviceAPI.getDeviceConfig(cbid);
      return configData;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get device config');
    }
  }
);

// ==================== OTA THUNKS ====================

// SOTA - Sampler OTA (command 103)
export const sendSotaCommand = createAsyncThunk(
  'devices/sendSota',
  async ({ cbid, devId }, { rejectWithValue }) => {
    try {
      const response = await deviceAPI.sendSotaCommand(cbid, devId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to send SOTA command');
    }
  }
);

// COTA - Control Board OTA (command 98)
export const sendCotaCommand = createAsyncThunk(
  'devices/sendCota',
  async ({ cbid, devId }, { rejectWithValue }) => {
    try {
      const response = await deviceAPI.sendCotaCommand(cbid, devId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to send COTA command');
    }
  }
);

// S3OTA - S3 OTA (command 107)
export const sendS3OtaCommand = createAsyncThunk(
  'devices/sendS3Ota',
  async ({ cbid, command = "107" }, { rejectWithValue }) => {
    try {
      const response = await deviceAPI.sendS3OtaCommand(cbid, command);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to send S3OTA command');
    }
  }
);

// ==================== TRANSFORM FUNCTION ====================

// Transform API data to component format
const transformDeviceData = (apiDevice) => {
  return {
    sno: apiDevice.sno,
    deviceId: apiDevice.cbid || apiDevice.sno,
    dealer: apiDevice.dealerName,
    dealerId: apiDevice.dealerid,
    handsetId: apiDevice.devId,
    samplerchipid: apiDevice.samplerchipid ?? apiDevice.schipid ?? '',
    controlchipid: apiDevice.controlchipid ?? apiDevice.cchipid ?? '',
    status: apiDevice.status,
    cam4: apiDevice.cam4 ?? '',
    autoTimeSet: apiDevice.autotimeset === 1 ? 'ENABLE' : 'DISABLE',
    timezone: apiDevice.timezone || 'GMT',
    cota: apiDevice.iswifi === 1,
    sota: true,
    s3ota: true,
    config: true,
    calibrationDate: apiDevice.calibrationdate,
    lastUpdate: apiDevice.lastupdate,
    testingduration: apiDevice.testingduration,
    lastOperation: apiDevice.lastoperation,
    lastOpDate: apiDevice.lastopdate,
    lastsentdate: apiDevice.lastsentdate || 'A200',
    iswifi: apiDevice.iswifi,
    autotimeset: apiDevice.autotimeset,
    dealerid: apiDevice.dealerid,
    cbid: apiDevice.cbid  // Keep original cbid for config API
  };
};

// ==================== SLICE ====================

const deviceSlice = createSlice({
  name: 'devices',
  initialState: {
    devices: [],
    loading: false,
    error: null,
    successMessage: null,
    userRole: 'user',
    isAdmin: false,
    totalRecords: 0,
    lastFetched: null,
    updateLoading: false,
    updateError: null,
    updateSuccess: false,
    deviceOffice: null,
    createLoading: false,
    createSuccess: false,
    deleteLoading: false,
    // OTA states
    otaLoading: false,
    otaResult: null,
    // Config states
    configData: null,
    configLoading: false,
    configDialogOpen: false,
    selectedConfigDevice: null,
    configSearch: ''
  },
  reducers: {
    clearDevices: (state) => {
      state.devices = [];
      state.totalRecords = 0;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearUpdateState: (state) => {
      state.updateError = null;
      state.updateSuccess = false;
      state.createSuccess = false;
      state.deviceOffice = null;
      state.successMessage = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearOtaResult: (state) => {
      state.otaResult = null;
    },
    clearConfigData: (state) => {
      state.configData = null;
    },
    openConfigDialog: (state, action) => {
      state.configDialogOpen = true;
      state.selectedConfigDevice = action.payload;
    },
    closeConfigDialog: (state) => {
      state.configDialogOpen = false;
      state.selectedConfigDevice = null;
      state.configData = null;
      state.configSearch = '';
    },
    setConfigSearch: (state, action) => {
      state.configSearch = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // ==================== FETCH DEVICES ====================
      .addCase(fetchDevices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.loading = false;
        const { devices, userRole, isAdmin } = action.payload;
        const transformedDevices = devices.map(transformDeviceData);
        state.devices = transformedDevices;
        state.userRole = userRole;
        state.isAdmin = isAdmin;
        state.totalRecords = transformedDevices.length;
        state.lastFetched = new Date().toISOString();
        console.log(`Loaded ${transformedDevices.length} devices`);
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch devices';
      })
      
      // ==================== FETCH DEALER DEVICES ====================
      .addCase(fetchDealerDevices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDealerDevices.fulfilled, (state, action) => {
        state.loading = false;
        const { devices } = action.payload;
        const transformedDevices = devices.map(transformDeviceData);
        state.devices = transformedDevices;
        state.totalRecords = transformedDevices.length;
      })
      .addCase(fetchDealerDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch dealer devices';
      })
      
      // ==================== CREATE DEVICE ====================
      .addCase(createDevice.pending, (state) => {
        state.createLoading = true;
        state.createSuccess = false;
        state.error = null;
      })
      .addCase(createDevice.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.successMessage = action.payload?.message || 'Device added successfully!';
      })
      .addCase(createDevice.rejected, (state, action) => {
        state.createLoading = false;
        state.createSuccess = false;
        state.error = action.payload || 'Failed to create device';
      })
      
      // ==================== EDIT DEVICE ====================
      .addCase(editDevice.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(editDevice.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;
        state.successMessage = action.payload?.message || 'Device updated successfully!';
      })
      .addCase(editDevice.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload || 'Failed to edit device';
        state.updateSuccess = false;
      })
      
      // ==================== DELETE DEVICE ====================
      .addCase(deleteDevice.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteDevice.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.successMessage = action.payload?.message || 'Device deleted successfully!';
      })
      .addCase(deleteDevice.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload || 'Failed to delete device';
      })
      
      // ==================== GET DEVICE OFFICE ====================
      .addCase(getDeviceOffice.fulfilled, (state, action) => {
        state.deviceOffice = action.payload;
      })
      
      // ==================== GET DEVICE CONFIG ====================
      .addCase(getDeviceConfig.pending, (state) => {
        state.configLoading = true;
        state.configData = null;
      })
      .addCase(getDeviceConfig.fulfilled, (state, action) => {
        state.configLoading = false;
        state.configData = action.payload;
      })
      .addCase(getDeviceConfig.rejected, (state, action) => {
        state.configLoading = false;
        state.configData = null;
        state.error = action.payload || 'Failed to fetch device config';
      })
      
      // ==================== SOTA (Sampler OTA) ====================
      .addCase(sendSotaCommand.pending, (state) => {
        state.otaLoading = true;
        state.otaResult = null;
      })
      .addCase(sendSotaCommand.fulfilled, (state, action) => {
        state.otaLoading = false;
        state.otaResult = action.payload;
      })
      .addCase(sendSotaCommand.rejected, (state, action) => {
        state.otaLoading = false;
        state.otaResult = { success: false, message: action.payload };
      })
      
      // ==================== COTA (Control Board OTA) ====================
      .addCase(sendCotaCommand.pending, (state) => {
        state.otaLoading = true;
        state.otaResult = null;
      })
      .addCase(sendCotaCommand.fulfilled, (state, action) => {
        state.otaLoading = false;
        state.otaResult = action.payload;
      })
      .addCase(sendCotaCommand.rejected, (state, action) => {
        state.otaLoading = false;
        state.otaResult = { success: false, message: action.payload };
      })
      
      // ==================== S3OTA ====================
      .addCase(sendS3OtaCommand.pending, (state) => {
        state.otaLoading = true;
        state.otaResult = null;
      })
      .addCase(sendS3OtaCommand.fulfilled, (state, action) => {
        state.otaLoading = false;
        state.otaResult = action.payload;
      })
      .addCase(sendS3OtaCommand.rejected, (state, action) => {
        state.otaLoading = false;
        state.otaResult = { success: false, message: action.payload };
      });
  }
});

// ==================== ACTIONS ====================
export const { 
  clearDevices, 
  clearError, 
  clearUpdateState,
  clearSuccessMessage,
  clearOtaResult,
  clearConfigData,
  openConfigDialog,
  closeConfigDialog,
  setConfigSearch
} = deviceSlice.actions;

// ==================== SELECTORS ====================
export const selectAllDevices = (state) => state.devices.devices;
export const selectDevicesLoading = (state) => state.devices.loading;
export const selectDevicesError = (state) => state.devices.error;
export const selectUserRole = (state) => state.devices.userRole;
export const selectIsAdmin = (state) => state.devices.isAdmin;
export const selectTotalDevices = (state) => state.devices.totalRecords;
export const selectUpdateLoading = (state) => state.devices.updateLoading;
export const selectUpdateError = (state) => state.devices.updateError;
export const selectUpdateSuccess = (state) => state.devices.updateSuccess;
export const selectDeviceOffice = (state) => state.devices.deviceOffice;
export const selectSuccessMessage = (state) => state.devices.successMessage;
export const selectCreateLoading = (state) => state.devices.createLoading;
export const selectCreateSuccess = (state) => state.devices.createSuccess;
export const selectDeleteLoading = (state) => state.devices.deleteLoading;
// OTA Selectors
export const selectOtaLoading = (state) => state.devices.otaLoading;
export const selectOtaResult = (state) => state.devices.otaResult;
// Config Selectors
export const selectConfigData = (state) => state.devices.configData;
export const selectConfigLoading = (state) => state.devices.configLoading;
export const selectConfigDialogOpen = (state) => state.devices.configDialogOpen;
export const selectSelectedConfigDevice = (state) => state.devices.selectedConfigDevice;
export const selectConfigSearch = (state) => state.devices.configSearch;

export default deviceSlice.reducer;
